import headerJson from '../Json/header';
import authJson from '../Json/auth';
import profileJson from '../Json/profile';
import sellcarFormJson from '../Json/sellcar_form';
import sellcarPageJson from '../Json/sellcar_page';
import valuationFormJson from '../Json/valuation_form';
import valuationPageJson from '../Json/valuation_page';
import comparePageJson from '../Json/compare_page';
import carTrustJson from '../Json/car_trust';
import emiPageJson from '../Json/emi_page';
import warrantyPageJson from '../Json/warranty_page';
import contactJson from '../Json/contact';
import privacyJson from '../Json/privacy';
import aboutusJson from '../Json/aboutus';
import homePageJson from '../Json/home_page';
import appPromoJson from '../Json/appPromo';
import { carousel as carouselJson, heroType as heroTypeJson } from '../Json/hero';
import { assetUrl } from '../Utils/imageUtils';

const buildDefaultInspectionCategories = () =>
  (carTrustJson.inspection?.defaultCategories || []).map(({ key, title }) => ({
    key,
    title,
    score: 0,
    total: 0,
    status: 'pending',
    items: [],
  }));

export const getDefaultInspectionCategories = buildDefaultInspectionCategories;

/** Fallback content when API/DB has no record yet — matches frontend Json structure. */
export const SITE_CONFIG_DEFAULTS = {
  header: headerJson,
  auth: authJson,
  profile: profileJson,
  sellcar: { form: sellcarFormJson, page: sellcarPageJson },
  valuation: { form: valuationFormJson, page: valuationPageJson },
  compare: { page: comparePageJson },
  carTrust: { page: carTrustJson },
  emi: { page: emiPageJson },
  warranty: warrantyPageJson,
};

export const CRUD_DEFAULTS = {
  contact: contactJson,
  privacy: privacyJson,
  about: aboutusJson,
  hero: { carousel: carouselJson, heroType: heroTypeJson },
  'home-page': homePageJson,
  'app-promo': appPromoJson,
  cars: {
    status: 'awaiting',
    price: 0,
    location: '',
    year: 2022,
    km_driven: 0,
    tire_wear: 0,
    youtube: '',
    shorts: '',
    home_show: true,
    is_modify: false,
    is_project: false,
    view_count: 0,
    images: [assetUrl('card1.jpg')],
    features: [],
    testDriveEnabled: true,
    warranty: {
      package: '',
      supportDays: 7,
      engineGearbox: false,
      returnEligible: false,
      returnDays: 0,
      inclusions: [],
      exclusions: [],
    },
    inspection: {
      isCertified: false,
      overallScore: 0,
      reportPdf: '',
      photos: [],
      totalPoints: 0,
      passedPoints: 0,
      summary: '',
      categories: buildDefaultInspectionCategories(),
      history: [],
    },
  },
};

export const getModuleDefaults = (module) => {
  if (!module) return {};
  if (module.siteConfigKey) {
    return SITE_CONFIG_DEFAULTS[module.siteConfigKey] || {};
  }
  return CRUD_DEFAULTS[module.schemaKey || module.key] || {};
};

const isBrokenNavItem = (item) =>
  !item || Array.isArray(item) || typeof item !== 'object' || !item.label || !item.path;

/** Repair header navItems if a bad save wiped menu link metadata */
export const normalizeHeaderConfig = (data) => {
  const defaults = SITE_CONFIG_DEFAULTS.header;
  if (!data || typeof data !== 'object') return { ...defaults };

  const nav = data.navItems;
  const needsRepair =
    !Array.isArray(nav) ||
    nav.length === 0 ||
    nav.some(isBrokenNavItem);

  return {
    ...defaults,
    ...data,
    logo: { ...defaults.logo, ...(data.logo || {}) },
    search: { ...defaults.search, ...(data.search || {}) },
    megaMenu: { ...defaults.megaMenu, ...(data.megaMenu || {}) },
    auth: { ...defaults.auth, ...(data.auth || {}) },
    navItems: needsRepair
      ? defaults.navItems.map((def, i) => {
          const cur = nav?.[i];
          if (cur && typeof cur === 'object' && !Array.isArray(cur)) {
            return { ...def, ...cur, label: cur.label || def.label };
          }
          return { ...def };
        })
      : nav.map((item, i) => ({ ...defaults.navItems[i], ...item })),
  };
};

export const normalizeSiteConfig = (key, data) => {
  if (key === 'header') return normalizeHeaderConfig(data);
  return data || getModuleDefaults({ siteConfigKey: key });
};

const isBrokenCarouselItem = (item) =>
  !item || typeof item !== 'object' || Array.isArray(item) || (!item.text && !item.image);

const isBrokenHeroTypeItem = (item) =>
  !item || typeof item !== 'object' || Array.isArray(item) || !item.name;

/** Repair hero carousel/buttons if a bad save wiped slide text or button labels */
export const normalizeHeroConfig = (data) => {
  const defaults = CRUD_DEFAULTS.hero;
  if (!data || typeof data !== 'object') return { ...defaults };

  const carousel = data.carousel;
  const heroType = data.heroType;

  const carouselNeedsRepair =
    !Array.isArray(carousel) || carousel.length === 0 || carousel.some(isBrokenCarouselItem);

  const heroTypeNeedsRepair =
    !Array.isArray(heroType) || heroType.length === 0 || heroType.some(isBrokenHeroTypeItem);

  return {
    ...defaults,
    ...data,
    carousel: carouselNeedsRepair
      ? defaults.carousel.map((def, i) => {
          const cur = carousel?.[i];
          if (cur && typeof cur === 'object' && !Array.isArray(cur)) {
            return {
              ...def,
              ...cur,
              text: cur.text || def.text,
              image: cur.image || def.image,
            };
          }
          return { ...def };
        })
      : carousel.map((item, i) => ({
          ...defaults.carousel[i],
          ...item,
          text: item.text || defaults.carousel[i]?.text,
          image: item.image || defaults.carousel[i]?.image,
        })),
    heroType: heroTypeNeedsRepair
      ? defaults.heroType.map((def, i) => {
          const cur = heroType?.[i];
          if (cur && typeof cur === 'object' && !Array.isArray(cur)) {
            return { ...def, ...cur, name: cur.name || def.name };
          }
          return { ...def };
        })
      : heroType.map((item, i) => ({
          ...defaults.heroType[i],
          ...item,
          name: item.name || defaults.heroType[i]?.name,
        })),
  };
};

export const MODULE_DESCRIPTIONS = {
  cars: 'Manage vehicle listings. Set status to Approved to publish. Add inspection data for the car details Inspection tab.',
  brands: 'Car manufacturers shown in filters and listings.',
  models: 'Models linked to a brand. Used in sell-car and browse filters.',
  'body-types': 'Vehicle body styles (SUV, Sedan, etc.).',
  'fuel-types': 'Fuel options (Petrol, Diesel, Electric, etc.).',
  transmissions: 'Gearbox types (Manual, Automatic, etc.).',
  seats: 'Seating capacity options.',
  'car-colors': 'Exterior color options for listings.',
  features: 'Feature tags shown on car detail pages.',
  'sub-registers': 'Sales territories linked to cities and partners.',
  states: 'States for location filters.',
  cities: 'Cities within a state.',
  users: 'Registered accounts. Role controls admin, partner, or user access.',
  ads: 'Homepage banner ads linking to a car listing.',
  'test-drives': 'Customer test drive booking requests.',
  'why-choose-us': 'Feature cards on the About / home sections.',
  contact: 'Contact page content (hero, office, email, phone).',
  privacy: 'Privacy policy page text and sections.',
  about: 'About us page content.',
  hero: 'Homepage carousel slides and hero action buttons.',
  'home-page': 'Homepage section titles and visibility.',
  'app-promo': 'Mobile app promotion block on the homepage.',
  'site-header': 'Site header: logo text, menu labels, search placeholder.',
  'site-auth': 'Login and signup page labels and messages.',
  'site-profile': 'User profile page tab labels and section text.',
  'site-sellcar': 'Sell car form labels and page copy.',
  'site-valuation': 'Car valuation form and page content.',
  'site-compare': 'Compare cars page labels.',
  'site-car-trust': 'Car trust / certification section copy.',
  'site-emi': 'EMI calculator page labels and text.',
  'site-warranty': 'Warranty and protection policy page content.',
  'protection-orders': 'Warranty / protection plan orders from customers.',
};
